import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
import { useRef } from 'react'
import { Plus } from 'lucide-react'
import { Button, Label, Textarea } from '@teach/ui'
import { QUIZ_LIMITS } from '@/features/editor/constants/fieldLimits'
import { charsOk } from '@/features/editor/lib/quizWordLimits'
import { CharCount } from './shared/QuizFieldCounters'
import { messagesNow, useMessages } from '@/i18n'
import { messages } from './messages'

const GAP = '___'

// Backend requires the statement to contain EXACTLY ONE `___` gap (3+
// underscores). Returns a soft warning when it doesn't (null = valid / empty).
const gapWarningText = (question: string): string | null => {
  if (question.trim().length === 0) return null
  const gaps = question.match(/_{3,}/g)?.length ?? 0
  // Нет пропуска — молчим: под полем кнопка «Добавить пропуск» говорит то же.
  if (gaps <= 1) return null
  return messagesNow(messages).oneGapOnly
}

interface Props {
  value: string
  onChange: (next: string) => void
}

/**
 * Утверждение квиза «Заполни пропуск»: поле, кнопка «Добавить пропуск» (вставляет
 * `___` в место курсора, а без фокуса — в конец).
 * Пропуск ровно один — кнопка гаснет, когда он уже есть.
 */
export const FillBlankStatement = ({ value, onChange }: Props) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const ref = useRef<HTMLTextAreaElement>(null)
  // Где был курсор, когда автор последний раз стоял в поле: клик по кнопке
  // уводит фокус, а у нетронутого поля позиция 0 — пропуск ушёл бы в начало.
  const caret = useRef<{ start: number; end: number } | null>(null)
  const rememberCaret = () => {
    const el = ref.current
    if (el) caret.current = { start: el.selectionStart, end: el.selectionEnd }
  }
  const hasGap = /_{3,}/.test(value)
  const gapWarning = gapWarningText(value)

  const insertGap = () => {
    const el = ref.current
    const start = Math.min(caret.current?.start ?? value.length, value.length)
    const end = Math.min(caret.current?.end ?? value.length, value.length)
    const before = value.slice(0, start)
    const after = value.slice(end)
    // Пробелы вокруг — только там, где их нет: «Столица —|» → «Столица — ___».
    const left = before && !/\s$/.test(before) ? ' ' : ''
    const right = after && !/^\s/.test(after) ? ' ' : ''
    const next = `${before}${left}${GAP}${right}${after}`
    if (!charsOk(next, QUIZ_LIMITS.FILL_BLANK_STATEMENT, value)) return
    onChange(next)
    const at = before.length + left.length + GAP.length
    caret.current = { start: at, end: at }
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(at, at)
    })
  }

  return (
    <section className="space-y-2">
      <Label htmlFor="fb-statement" className="font-medium text-foreground">
        {m.statement}
      </Label>
      <Textarea
        ref={ref}
        id="fb-statement"
        value={value}
        onSelect={rememberCaret}
        onChange={(e) => {
          if (charsOk(e.target.value, QUIZ_LIMITS.FILL_BLANK_STATEMENT, value)) {
            onChange(e.target.value)
          }
        }}
        maxLength={QUIZ_LIMITS.FILL_BLANK_STATEMENT}
        placeholder={cm.fillBlankPlaceholder}
        rows={3}
        className="resize-none rounded-xl"
      />
      <CharCount value={value} max={QUIZ_LIMITS.FILL_BLANK_STATEMENT} />
      {gapWarning ? <p className="text-xs text-warning">{gapWarning}</p> : null}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-full"
        onClick={insertGap}
        disabled={hasGap}
      >
        <Plus className="mr-1.5 size-4" />
        {m.addGap}
      </Button>
    </section>
  )
}
